import classnames from 'classnames2';
import React from 'react';
import tabbable from 'tabbable';

import { Description } from '../Description';
import { Header } from '../Header';
import { Icon } from '../Icon';
import { Illustration, IllustrationTypes } from '../Illustration';

import styles from './Dialog.module.scss';

export enum DialogTypes {
  Info = 'Info',
  Destructive = 'Destructive',
  Warning = 'Warning'
}

export interface IDialogState {
  activeIndex: number;
  dialogTriggerElement: HTMLButtonElement;
}

export interface IDialog {
  dataTest?: string;
  description?: any;
  illustration?: IllustrationTypes;
  title?: string;
  width?: 'narrow' | 'default' | 'wide';
  variant?: 'info' | 'destructive' | 'warning';
  onClose?(event): void;
}

export const DialogFooter = ({ children }) => <div className={styles.dialogFooter}>{children}</div>;

function createDialog(displayName, defaultDataTest): React.ComponentClass<IDialog, IDialogState> {
  class DialogKind extends React.Component<IDialog, IDialogState> {
    static displayName = displayName;
    contentRef = React.createRef<any>();
    closeButtonRef = React.createRef<any>();
    state = {
      activeIndex: 0,
      dialogTriggerElement: null
    };

    componentDidMount() {
      if (document) {
        this.setState({ dialogTriggerElement: document.activeElement as HTMLButtonElement });
      }
      if (this.closeButtonRef && this.closeButtonRef.current) {
        this.closeButtonRef.current.focus();
      }
    }

    componentWillUnmount() {
      const { dialogTriggerElement } = this.state;
      if (dialogTriggerElement && dialogTriggerElement.focus) {
        dialogTriggerElement.focus();
      }
    }

    findTabbableElements = () => {
      const contentTabbableElements =
        this.contentRef && this.contentRef.current ? tabbable(this.contentRef.current) : [];
      return this.closeButtonRef && this.closeButtonRef.current
        ? [this.closeButtonRef.current, ...contentTabbableElements]
        : contentTabbableElements;
    };

    handleKeydown = (event) => {
      const { activeIndex } = this.state;
      if (event.key === 'Escape') {
        this.handleClose(event);
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        let nextIndex;
        if (event.shiftKey) {
          nextIndex = activeIndex === 0 ? tabbableElements.length - 1 : activeIndex - 1;
        } else {
          nextIndex = (activeIndex + 1) % tabbableElements.length;
        }
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
      if (event.key === 'End') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        const nextIndex = tabbableElements.length - 1;
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
      if (event.key === 'Home') {
        event.preventDefault();
        const tabbableElements = this.findTabbableElements();
        const nextIndex = 0;
        this.setState({ activeIndex: nextIndex });
        tabbableElements[nextIndex].focus();
      }
    };

    handleClose = (event) => {
      const { onClose } = this.props;
      if (onClose) {
        onClose(event);
      }
    };

    render() {
      const { children, dataTest, description, illustration, onClose, title, width = 'default', variant } = this.props;

      const dialogDataTest = dataTest ? dataTest : defaultDataTest;

      const dialogVariant = variant ? variant : displayName;

      const dialogHeader = title ? (
        <div className={styles.dialogHeader}>
          <Header id="dialogTitle" as="h1" variant="h2">
            {title}
          </Header>
          {description ? <Description className={styles.dialogDescription}>{description}</Description> : null}
        </div>
      ) : null;

      return (
        <div data-test={`dialog-${dialogDataTest}`}>
          <div className={styles.pageCover} />
          <div className={styles.flexy}>
            <div
              onKeyDown={this.handleKeydown}
              role="dialog"
              aria-labelledby={title ? 'dialogTitle' : null}
              data-test={`dialog-${dialogDataTest}-container`}
              className={classnames(styles.dialogContainer, {
                [styles.variantDestructive]: dialogVariant === 'destructive',
                [styles.variantInfo]: dialogVariant === 'info',
                [styles.variantWarning]: dialogVariant === 'warning',
                [styles.widthDefault]: width === 'default',
                [styles.widthNarrow]: width === 'narrow',
                [styles.widthWide]: width === 'wide'
              })}
            >
              <div className={styles.dialogCloseBar}>
                {onClose && (
                  <button
                    className={styles.dialogCloseButton}
                    data-test={`dialog-${dialogDataTest}-close-button`}
                    ref={this.closeButtonRef}
                    tabIndex={0}
                    role="button"
                    onClick={this.handleClose}
                  >
                    <Icon className={styles.dialogCloseIcon} icon="action-close" ariaLabel="Close dialog" />
                  </button>
                )}
              </div>
              <div ref={this.contentRef} className={styles.dialogContentContainer}>
                {illustration ? (
                  <div className={styles.dialogIllustration}>
                    <Illustration illustration={illustration} ariaHidden={true} />
                  </div>
                ) : null}
                <div className={styles.dialogContent}>
                  {dialogHeader}
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return DialogKind;
}

export const baseDialog: React.ComponentClass<IDialog, IDialogState> = createDialog('', '');

export const Dialog = Object.assign(baseDialog, {
  Destructive: createDialog('destructive', 'destructive'),
  Footer: DialogFooter,
  Info: createDialog('info', 'info'),
  Warning: createDialog('warning', 'warning')
});
